import {
	autorun,
	computed,
	createAtom,
	onBecomeObserved,
	onBecomeUnobserved,
	runInAction,
	transaction,
	untracked,
} from "mobx";
import {
	ReactiveLayer,
	Reaction,
	ReactionOptions,
	ComputedAtomOptions,
	ComputedAtom,
} from "fluidstate";

/**
 * Returns a `ReactiveLayer` implementation backed by the MobX library.
 * This function provides the bridge between `fluidstate`'s abstract reactivity
 * API and MobX's concrete implementation.
 */
export const getReactiveLayer = (): ReactiveLayer => ({
	createAtom: (name, options) => {
		const atom = createAtom(
			name,
			options?.onBecomeObservedListener,
			options?.onBecomeUnobservedListener
		);
		return {
			reportObserved: () => atom.reportObserved(),
			reportChanged: () => atom.reportChanged(),
		};
	},

	createComputedAtom: function <T>(
		name: string,
		calculate: () => T,
		options?: ComputedAtomOptions
	): ComputedAtom<T> {
		const computedAtom = computed(calculate, {
			name,
			equals: options?.equals,
		});

		// MobX's `computed` does not accept observer listeners in its options,
		// so we attach them separately using `onBecomeObserved` and `onBecomeUnobserved`.
		if (options?.onBecomeObservedListener) {
			onBecomeObserved(computedAtom, options.onBecomeObservedListener);
		}

		if (options?.onBecomeUnobservedListener) {
			onBecomeUnobserved(computedAtom, options.onBecomeUnobservedListener);
		}

		return {
			get: () => {
				return computedAtom.get();
			},
		};
	},

	createReaction: (effect: () => void, options?: ReactionOptions): Reaction => {
		const dispose = autorun(effect, {
			scheduler: options?.scheduler,
		});

		return {
			stop: () => {
				dispose();
			},
		};
	},

	untrack: <T>(action: () => T): T => {
		return untracked(action);
	},

	runTransaction: <T>(transactionFn: () => T): T => {
		return transaction(transactionFn);
	},

	runAction: <T>(action: () => T): T => {
		return runInAction(action);
	},
});
